// src/lib/signed-line.js
//
// Single job (#1040 PR D0; moved here by #1132 D1.1): own the "TEAM ±N" grammar a
// sportsbook prints for a spread / puck-line / run-line selection, and the rule that
// turns a (pick, line) pair into the line that is PERSISTED, so the sign of the line
// survives to the write boundary. Pure and league-blind: callers gate on
// isTeamPickMarket(market) && isLineRequired(market, league).
//
// Shared by the server (api/lib/split-signed-pick.js re-exports it for Stage C of the
// vision pipeline; api/lib/normalize-leg-pick.js and api/import-confirm.js import it)
// and the client (src/lib/save-blockers.js), so the confirm screen computes exactly
// the line import-confirm's storedLineFor will write -- parity by construction, not
// by a mirrored copy (#1132 D1).
//
// Reads: strings / numbers. Writes: nothing. Does NOT resolve teams, markets, or grade.

// Team, whitespace, then EXACTLY one trailing token: a signed number or a
// pick'em word. `$`-anchored so the LAST token is the line.
const SIGNED_LINE_RE = /^(.+?)\s+([+-]\d+(?:\.\d+)?|PK|PICK|EVEN)$/i;
// If the team part still carries a signed number or a pick'em word, the trailing
// token was odds ("Chiefs -3.5 -110") or a duplicate -- fail closed (Ultron gap 2).
const TEAM_PART_DIRTY = /[+-]\d|\b(PK|PICK|EVEN)\b/i;
export const PICKEM_RE = /^(PK|PICK|EVEN)$/i;
// American odds are never below 100 in magnitude; spreads never reach it.
const MAX_SPREAD_MAGNITUDE = 100;
// #1040 pre-Codex r1 Sec-1: cleanText's `\s*\(match\)\s*$` and SIGNED_LINE_RE's
// `(.+?)\s+…$` are quadratic on long whitespace runs -- measured 10.7s through
// buildLegRows on a 60k-space pick (client-controlled string, e.g. a
// SlipImporter correction or a malformed vision evidence field). Both
// splitSignedPick and normalizeRawLine short-circuit above this length before
// any regex runs. No legitimate sportsbook pick/line text approaches 120 chars.
const MAX_INPUT_LEN = 120;

function cleanText(v) {
  // Trim first (linear, no backtracking), then a bounded `(match)` suffix
  // strip anchored to the end with no leading `\s*` to backtrack over, then
  // trim again (the suffix strip can leave a trailing space), then the
  // unicode minus / en dash normalization last.
  return String(v ?? '')
    .trim()
    .replace(/\(match\)$/i, '')     // BetMGM SGP suffix
    .trim()
    .replace(/[−–]/g, '-');         // unicode minus / en dash -> ASCII
}

/**
 * "Kansas City Chiefs -3.5" -> { pick: 'Kansas City Chiefs', line: -3.5, source: 'pick' }
 * "Kansas City Chiefs PK"   -> { pick: 'Kansas City Chiefs', line: 0,    source: 'pickem' }
 * Anything else -> null (caller leaves the leg untouched).
 */
export function splitSignedPick(pick) {
  if (typeof pick !== 'string') return null;
  if (pick.length > MAX_INPUT_LEN) return null;
  const text = cleanText(pick);
  const m = text.match(SIGNED_LINE_RE);
  if (!m) return null;
  const team = m[1].trim();
  if (!team || TEAM_PART_DIRTY.test(team)) return null;
  if (PICKEM_RE.test(m[2])) return { pick: team, line: 0, source: 'pickem' };
  const line = Number(m[2]);
  if (!Number.isFinite(line) || Math.abs(line) >= MAX_SPREAD_MAGNITUDE) return null;
  // Number('-0') is -0; store a plain 0 so JSON/DB never carry a negative zero.
  return { pick: team, line: line === 0 ? 0 : line, source: 'pick' };
}

/**
 * Classify a raw line value (lineTextRaw string, or an already-numeric line).
 *  { line: number, signed: true }                  -- a usable signed line (0 counts as signed: pick'em)
 *  { line: <as given>, signed: false, unsigned: true } -- a positive value with no sign evidence
 *  { line: null, signed: false }                   -- absent / unparseable
 */
export function normalizeRawLine(raw) {
  if (raw === null || raw === undefined) return { line: null, signed: false };
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return { line: null, signed: false };
    // #1040 pre-Codex r1 FC-3: odds mis-routed into lineTextRaw ("-110") are
    // never a real spread -- same MAX_SPREAD_MAGNITUDE guard splitSignedPick
    // already applies, mirrored here for the raw-line path.
    if (Math.abs(raw) >= MAX_SPREAD_MAGNITUDE) return { line: null, signed: false };
    if (raw === 0) return { line: 0, signed: true };
    return raw < 0 ? { line: raw, signed: true } : { line: raw, signed: false, unsigned: true };
  }
  if (typeof raw !== 'string' || raw.length > MAX_INPUT_LEN) return { line: null, signed: false };
  const text = cleanText(raw);
  if (!text) return { line: null, signed: false };
  if (PICKEM_RE.test(text)) return { line: 0, signed: true };
  if (/^[+-]\d+(?:\.\d+)?$/.test(text)) {
    const n = Number(text);
    if (Math.abs(n) >= MAX_SPREAD_MAGNITUDE) return { line: null, signed: false };
    return { line: n === 0 ? 0 : n, signed: true };
  }
  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const n = Number(text);
    // #1040 pre-Codex r2 FIX 1 (P1): the number branch and the signed-string
    // branch above both got the MAX_SPREAD_MAGNITUDE guard in r1; this
    // unsigned-STRING branch did not, so odds mis-routed here as an unsigned
    // string (e.g. '110') were kept as a "line" -- calcStatus's |line| >= 100
    // spread math then grades BOTH sides won.
    if (n >= MAX_SPREAD_MAGNITUDE) return { line: null, signed: false };
    // #1040 pre-Codex r2 FIX 2 (P2 Sec-1): return the CLEANED number, not the
    // raw (possibly dirty) input -- a suffix like "1.5(match)" must not reach
    // leg.line verbatim. applySignedSpread / normalizeLegPick now assign this
    // value directly.
    return n === 0 ? { line: 0, signed: true } : { line: n, signed: false, unsigned: true };
  }
  return { line: null, signed: false };
}

/**
 * The line a LINE-REQUIRED team-pick leg carries after write-boundary normalization
 * (extracted byte-equivalent from normalizeLegPick, #1132 D1.1). Recovers the line
 * from a pick that still carries its signed suffix, and nulls it (fail closed) when
 * the pick's sign disagrees with a signed raw line -- the same rule Stage C's
 * SPREAD_SIGN_CONFLICT enforces. An absent line ('' / null / undefined) with no
 * split is returned unchanged. The PICK half of the split stays with the caller.
 */
export function resolveSpreadLine(pick, line) {
  const split = splitSignedPick(pick);
  const raw = normalizeRawLine(line);
  if (split && raw.signed && raw.line !== split.line) {
    return null;                       // conflicting signs: fail closed (same rule as Stage C)
  } else if (raw.signed) {
    return raw.line;                   // 'EVEN' -> 0, '-3.5' -> -3.5 (number)
  } else if (split) {
    // #1040 pre-Codex r2 FIX 4: when raw is unsigned/absent, the pick's line
    // wins regardless of whether an unsigned raw's magnitude agrees with it --
    // Stage C raises UNSIGNED_SPREAD_LINE on a magnitude disagreement via
    // pushOnce; this boundary has no warnings array, so it silently keeps the
    // pick's line here.
    return split.line;                 // absent / unsigned raw yields to the pick's sign
  } else if (raw.unsigned) {
    // #1040 pre-Codex r2 FIX 2 (P2 Sec-1): the CLEANED value (raw.line), never the
    // dirty original -- a suffix like "1.5(match)" must not reach bet_legs.line /
    // bets.line verbatim.
    return raw.line;
  } else if (!raw.unsigned && line != null && line !== '') {
    // #1040 pre-Codex r2 FIX 3 (P2 FC-2): a bare pick with an unparseable /
    // out-of-range raw line (comma decimal, odds magnitude, a boolean, ...) is
    // garbage, not a line -- null. Same rule as applySignedSpread's FC-2 tail.
    return null;
  }
  return line;
}

/**
 * The numeric form of a line as PERSISTED (moved from api/import-confirm.js by #1132
 * D1.1). NOTHING non-numeric may reach bet_legs.line / bets.line (numeric) -- a
 * 'PK' / 'EVEN' / stray odds string was a 22P02 for the whole import (Ultron gap 16).
 *
 * `Number.isFinite(Number(v))` is porous: `Number('  ')` is 0, `Number(true)` is
 * 1, `Number([])` is 0, and `Number('0x10')` is 16 -- all pass that check and
 * were returned VERBATIM (a boolean or an array reaching a numeric Postgres
 * column is still a 22P02). A numeric string is explicitly validated against
 * an optional-sign decimal shape and RETURNED AS A NUMBER -- Postgres numeric
 * accepts a JS number just as readily as the string, and this closes the
 * porosity without a second string-shaped code path.
 */
export function numericLineOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number'
    ? v
    : (typeof v === 'string' && /^\s*[+-]?\d+(?:\.\d+)?\s*$/.test(v) ? Number(v) : NaN);
  return Number.isFinite(n) ? n : null;
}
